import React from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Factory, Building2, Home, User, Video,
  HeadphonesIcon, CheckCircle, GraduationCap, PenTool
} from 'lucide-react';

const Section = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <section className={`py-16 md:py-24 px-4 ${className}`}>
    <div className="container mx-auto max-w-6xl">
      {children}
    </div>
  </section>
);

const ServiceCard = ({ title, icon: Icon, desc, details, colorClass, delay }: { title: string, icon: any, desc: string, details?: string[], colorClass: string, delay: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    className="bg-white dark:bg-secondary-800 rounded-2xl p-8 shadow-lg border border-secondary-100 dark:border-secondary-700 hover:shadow-xl transition-all h-full flex flex-col"
  >
    <div className={`w-14 h-14 rounded-xl ${colorClass} bg-opacity-10 dark:bg-opacity-20 flex items-center justify-center mb-6`}>
      <Icon size={32} className={colorClass.replace('bg-', 'text-').replace('-500', '-600')} />
    </div>
    <h3 className="text-2xl font-bold mb-4 text-secondary-900 dark:text-white">{title}</h3>
    <p className="text-secondary-600 dark:text-secondary-300 mb-6 leading-relaxed flex-grow">
      {desc}
    </p>
    {details && (
      <div className="pt-6 border-t border-secondary-100 dark:border-secondary-700 mt-auto">
        <h4 className="font-semibold mb-3 text-sm text-secondary-500 uppercase tracking-wider">Includes:</h4>
        <ul className="space-y-2">
          {details.map((item, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-400">
              <CheckCircle size={14} className="text-primary-500" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    )}
  </motion.div>
);

const Services = () => {
  return (
    <div className="bg-secondary-50 dark:bg-secondary-900 min-h-screen">
      {/* Hero */}
      <div className="bg-white dark:bg-secondary-900 py-20 px-4 text-center border-b border-secondary-200 dark:border-secondary-800">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-6xl font-bold mb-6 gradient-text"
        >
          Our Services
        </motion.h1>
        <p className="text-xl text-secondary-600 dark:text-secondary-400 max-w-2xl mx-auto mb-8">
          Comprehensive manpower and security solutions tailored for excellence.
        </p>
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-primary-50 dark:bg-primary-900/10 text-primary-700 dark:text-primary-400 rounded-full font-medium">
          <HeadphonesIcon size={20} />
          <span>Contact Support: 9852891115</span>
        </div>
      </div>

      <Section>
        {/* Manpower Services Grid */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold mb-12 text-center text-secondary-900 dark:text-white">Manpower Solutions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              "Security Guard", "House Keeping", "Office Staff", "Waiter", "Masalchi", "Cook"
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                className="bg-white dark:bg-secondary-800 p-6 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 text-center font-medium text-secondary-700 dark:text-secondary-200"
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Core Security Services */}
        <h2 className="text-3xl font-bold mb-12 text-center text-secondary-900 dark:text-white">Specialized Security</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          <ServiceCard
            title="Industrial Security"
            icon={Factory}
            colorClass="bg-blue-500"
            delay={0}
            desc='We undertake contracts with leading Industries to provide full proof static activities. We identify and assess the threat to devise "need based security" and "security in depth". Our personnel are expert in facing security risks including threats, violence, theft, and sabotage.'
          />
          <ServiceCard
            title="Corporate Security"
            icon={Building2}
            colorClass="bg-green-500"
            delay={0.1}
            desc='Specialized Security Gadgets Installation and protection for corporate. We provide experts to install Security Gadgets. Our trained personnel advise, install and monitor C.C.T.V. Systems for covert surveillance or conspicuous monitoring, including remote monitoring capabilities.'
          />
          <ServiceCard
            title="Residential Security"
            icon={Home}
            colorClass="bg-yellow-500"
            delay={0.2}
            desc='Reliable and efficient Residential Security Guard Services. Guards are trained on various parameters to provide emergency security and safety. Thorough background checks are performed before appointment.'
          />
          <ServiceCard
            title="Escort Services"
            icon={Shield}
            colorClass="bg-red-500"
            delay={0.3}
            desc='Armed and unarmed security guard services available 24/7. Our carefully recruited security officers are professional, responsive, and reliable. All officers pass background investigations including criminal, credit, and reference checks.'
          />
          <ServiceCard
            title="Security Consultancy"
            icon={User}
            colorClass="bg-purple-500"
            delay={0.4}
            desc='Expert consultants work with enterprises to design build end-to-end security solutions. Knowledge in Identity Governance, Vulnerability Risk Management, and Penetration Testing to deter, detect, and remediate threats.'
          />
          <ServiceCard
            title="Training Centre"
            icon={GraduationCap}
            colorClass="bg-cyan-500"
            delay={0.5}
            desc='Our own Modern Training Centre at Gauri Chowk, Patna, equipped to train 100 Security Personals per session for 21 days (As per PSARA guidelines). Addressing the need for professionally trained security personnel in the market.'
          />
          <ServiceCard
            title="Housekeeping"
            icon={PenTool}
            colorClass="bg-teal-500"
            delay={0.6}
            desc='Deploying trained men to perform tasks efficiently. We improve productivity by providing equipment and retraining. We regularly monitor and support employee performance.'
            details={['Janitorial Staff', 'Gardeners', 'Plumbers', 'Pantry', 'Electricians', 'Carpenters']}
          />
          <ServiceCard
            title="Corporate Staffing"
            icon={Building2}
            colorClass="bg-indigo-500"
            delay={0.7}
            desc='Providing qualified staff for corporate needs. We supervise independent units to ensure tasks are efficiently performed within a given time frame.'
            details={['Receptionists', 'Computer Operators', 'Chauffeurs', 'Office assistants']}
          />
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 bg-primary-600 rounded-3xl p-12 text-center text-white relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-3xl font-bold mb-4">Need a Custom Solution?</h3>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              We also offer MoU Pasara Consultancy Services and Security Guard Training Centre partnerships.
            </p>
            <button className="px-8 py-3 bg-white text-primary-600 font-bold rounded-full hover:bg-primary-50 transition-colors">
              Get in Touch
            </button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Services;